import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  FormControlLabel,
  SxProps,
  Theme,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CustomButton from '../../CustomButton/CustomButton';
import { popupActionButtonSx } from '../popupButtonStyles';
import { popupInputLabelSx } from '../popupInputStyles';
import { useModal } from '../../../../../store/ModalProvider';
import type { ServiceDetailModalData } from '../../../../../types/modal';

type InfoFieldProps = {
  label?: string;
  value?: string;
  endAdornment?: React.ReactNode;
  sx?: SxProps<Theme>;
  multiline?: boolean;
};

const defaultServiceDetail: ServiceDetailModalData = {
  serviceId: '0000',
  startCommercial: '02/05/2024 09:00',
  startSchedule: '02/05/2024 09:00',
  startLocation: 'Ericeira',
  endCommercial: '02/05/2024 10:00',
  endSchedule: '02/05/2024 10:00',
  endLocation: 'Mafra',
  vehicle: '0046',
  capacity: '51',
  kmsCarga: '10',
  kmsVazio: '2',
  kmsTotal: '12',
  serviceType: 'Aluguer Fixo/Carreira',
  serviceCode: '0090000',
  requestCode: '1115_PE2_PNE2',
  observations:
    'Tristique massa et dui nulla interdum. Et ac nibh placerat justo est enim',
  observationsSecondary:
    'Tristique massa et dui nulla interdum. Et ac nibh placerat justo est enim',
  status: {
    escalado: true,
    naoRealizado: false,
    folhaGravada: false,
    faturado: false,
  },
  serviceValue: '72,50',
  driverHoursCost: '6,90',
  invoice: {
    number: '45678987654',
    date: '10/05/2024',
    value: '6572,00',
  },
  driver: {
    name: '0109 - Agostinho Ferreira',
    phone: '910000000',
  },
  secondDriver: {
    name: '2º Motorista',
    phone: 'Telefone',
  },
  subcontract: {
    enabled: false,
    company: 'Empresa subcontratada',
    invoiceValue: 'Valor da fatura do Fornecedor',
  },
};

const sectionTitleSx = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: 1.3,
  color: '#092056',
};

const dialogTitleSx = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: 1.75,
  color: '#092056',
  letterSpacing: '0.15px',
};

const readonlyFieldSx = {
  '& .MuiInputLabel-root': {
    ...popupInputLabelSx,
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F7FC',
    borderRadius: '4px',
    minHeight: '40px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#092056',
    '& fieldset': {
      borderColor: 'rgba(134, 149, 175, 0)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(134, 149, 175, 0)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(134, 149, 175, 0)',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
    lineHeight: 1.3,
    WebkitTextFillColor: '#092056',
  },
  '& .MuiOutlinedInput-inputMultiline': {
    padding: '8px 12px',
  },
};

const infoLabelSx = {
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: 1.57,
  color: '#092056',
};

const checkboxLabelSx = {
  fontFamily: 'Poppins, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#092056',
};

const buttonSx = {
  background: '#00A5FF',
  textTransform: 'none' as const,
  padding: '4px 10px',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '22px',
  minHeight: '32px',
  '&:hover': {
    background: '#0090E0',
  },
};

const checkboxSx = {
  color: '#8695AF',
  '&.Mui-checked': {
    color: '#00A5FF',
  },
};

const InfoField = ({ label, value, endAdornment, sx, multiline }: InfoFieldProps) => (
  <TextField
    label={label}
    value={value || '--'}
    size="small"
    multiline={multiline}
    minRows={multiline ? 2 : undefined}
    InputProps={{ readOnly: true, endAdornment }}
    InputLabelProps={{ shrink: Boolean(label) }}
    sx={{ ...readonlyFieldSx, width: '100%', ...(sx ?? {}) }}
  />
);

const CustomModalServiceDetail: React.FC = () => {
  const { isOpen, closeModal, modalData } = useModal();
  const detailData = modalData as ServiceDetailModalData | undefined;

  const data = useMemo(() => {
    const incoming = detailData ?? defaultServiceDetail;
    return {
      ...defaultServiceDetail,
      ...incoming,
      status: { ...defaultServiceDetail.status, ...incoming.status },
      invoice: { ...defaultServiceDetail.invoice, ...incoming.invoice },
      driver: { ...defaultServiceDetail.driver, ...incoming.driver },
      secondDriver: { ...defaultServiceDetail.secondDriver, ...incoming.secondDriver },
      subcontract: { ...defaultServiceDetail.subcontract, ...incoming.subcontract },
    };
  }, [detailData]);

  if (!detailData) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={closeModal}
      maxWidth={false}
      sx={{ pointerEvents: 'auto', zIndex: 2000 }}
      PaperProps={{
        sx: {
          width: 'min(1200px, 100%)',
          borderRadius: '4px',
          backgroundColor: '#FFFFFF',
          boxShadow:
            '0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)',
          overflow: 'hidden',
          zIndex: 2001,
        },
      }}
    >
      <Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
        <Box sx={{ padding: '16px 24px 8px' }}>
          <Typography sx={dialogTitleSx}>Detalhe do Servico</Typography>
        </Box>

        <Box
          sx={{
            padding: '8px 24px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography sx={sectionTitleSx}>Servico</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <InfoField label="Inicio (Comercial)" value={data.startCommercial} sx={{ width: '171px' }} />
              <InfoField label="Inicio (Escalas)" value={data.startSchedule} sx={{ width: '171px' }} />
              <InfoField label="Local" value={data.startLocation} sx={{ flex: '1 1 260px' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <InfoField label="Termino (Comercial)" value={data.endCommercial} sx={{ width: '171px' }} />
              <InfoField label="Termino (Escalas)" value={data.endSchedule} sx={{ width: '171px' }} />
              <InfoField label="Local" value={data.endLocation} sx={{ flex: '1 1 260px' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <CustomButton size="small" sx={{ ...buttonSx, width: '171px' }}>
                Itinerario
              </CustomButton>
              <InfoField label="Viatura" value={data.vehicle} sx={{ width: '171px' }} />
              <InfoField label="Lotacao" value={data.capacity} sx={{ width: '120px' }} />
              <InfoField label="Kms Carga" value={data.kmsCarga} sx={{ width: '120px' }} />
              <InfoField label="Kms Vazio" value={data.kmsVazio} sx={{ width: '120px' }} />
              <InfoField label="Kms Total" value={data.kmsTotal} sx={{ width: '120px' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <InfoField
                label="Tipo de Servico"
                value={data.serviceType}
                endAdornment={<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#00A5FF' }} />}
                sx={{ width: '255px' }}
              />
              <InfoField label="Servico" value={data.serviceCode} sx={{ flex: '1 1 200px' }} />
              <InfoField label="Chapa/Requisicao" value={data.requestCode} sx={{ flex: '1 1 220px' }} />
            </Box>
            <InfoField label="Observacoes" value={data.observations} multiline />
            <InfoField value={data.observationsSecondary} multiline />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <Typography sx={infoLabelSx}>Estado do Servico</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={data.status?.escalado}
                    sx={checkboxSx}
                  />
                }
                label={<Typography sx={checkboxLabelSx}>Escalado</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={data.status?.naoRealizado}
                    sx={checkboxSx}
                  />
                }
                label={<Typography sx={checkboxLabelSx}>Servico nao realizado</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={data.status?.folhaGravada}
                    sx={checkboxSx}
                  />
                }
                label={<Typography sx={checkboxLabelSx}>Folha de ponto gravada</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={data.status?.faturado}
                    sx={checkboxSx}
                  />
                }
                label={<Typography sx={checkboxLabelSx}>Faturado</Typography>}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <Typography sx={infoLabelSx}>Valor do Servico</Typography>
              <InfoField label="Valor" value={data.serviceValue} sx={{ width: '200px' }} />
              <Typography sx={infoLabelSx}>Custo horas de trabalho Motorista</Typography>
              <InfoField label="Custo" value={data.driverHoursCost} sx={{ width: '200px' }} />
              <CustomButton size="small" sx={buttonSx}>
                Custo Detalhado
              </CustomButton>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <Typography sx={infoLabelSx}>Fatura</Typography>
              <InfoField label="Nr Fatura" value={data.invoice?.number} sx={{ width: '200px' }} />
              <InfoField label="Data" value={data.invoice?.date} sx={{ width: '200px' }} />
              <InfoField label="Valor" value={data.invoice?.value} sx={{ width: '200px' }} />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography sx={sectionTitleSx}>Motorista</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={data.subcontract?.enabled}
                  sx={checkboxSx}
                />
              }
              label={
                <Typography sx={checkboxLabelSx}>
                  Servico efetuado por empresa externa/subcontratado
                </Typography>
              }
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <InfoField
                label="Motorista"
                value={data.driver?.name}
                endAdornment={<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#00A5FF' }} />}
                sx={{ width: '255px' }}
              />
              <InfoField label="Telefone" value={data.driver?.phone} sx={{ width: '200px' }} />
              <CustomButton size="small" sx={buttonSx}>
                Ficha do Motorista
              </CustomButton>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <InfoField
                label="2º Motorista"
                value={data.secondDriver?.name}
                endAdornment={<KeyboardArrowDownIcon sx={{ fontSize: 20, color: '#00A5FF' }} />}
                sx={{ width: '255px' }}
              />
              <InfoField label="Telefone" value={data.secondDriver?.phone} sx={{ width: '200px' }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
              <InfoField label="Empresa subcontratada" value={data.subcontract?.company} sx={{ flex: '1 1 360px' }} />
              <InfoField
                label="Valor da fatura do Fornecedor"
                value={data.subcontract?.invoiceValue}
                sx={{ flex: '1 1 240px' }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '6px 16px 12px',
            backgroundColor: '#FFFFFF',
          }}
        >
          <CustomButton
            variant="contained"
            onClick={closeModal}
            sx={popupActionButtonSx}
          >
            Fechar
          </CustomButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CustomModalServiceDetail;
